import { useEffect, useState, useCallback } from "react";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from "reactflow";
import "reactflow/dist/style.css";
import Layout from "../components/Layout";
import API from "../api/axios";
import useSocket from "../hooks/useSocket";

// Custom Device Node
const DeviceNode = ({ data }) => {
  const colors = {
    ONLINE: { bg: "#dcfce7", border: "#16a34a", dot: "#16a34a" },
    OFFLINE: { bg: "#fee2e2", border: "#dc2626", dot: "#dc2626" },
    UNKNOWN: { bg: "#f3f4f6", border: "#9ca3af", dot: "#9ca3af" },
  };

  const color = colors[data.status] || colors.UNKNOWN;

  const icons = {
    router: "🔀",
    switch: "🔁",
    server: "🖥️",
    access_point: "📡",
    firewall: "🛡️",
  };

  return (
    <div
      style={{
        background: color.bg,
        border: `2px solid ${color.border}`,
      }}
      className="rounded-xl px-4 py-3 shadow-md min-w-[140px] text-center"
    >
      <div className="text-2xl mb-1">{icons[data.type] || "📦"}</div>
      <p className="text-xs font-bold text-gray-800">{data.name}</p>
      <p className="text-xs text-gray-500">{data.ipAddress}</p>
      <div className="flex items-center justify-center gap-1 mt-1">
        <div
          style={{ background: color.dot }}
          className="w-2 h-2 rounded-full"
        />
        <span style={{ color: color.dot }} className="text-xs font-medium">
          {data.status}
        </span>
      </div>
      {data.latency && (
        <p className="text-xs text-gray-400 mt-1">{data.latency}ms</p>
      )}
    </div>
  );
};

const nodeTypes = { deviceNode: DeviceNode };

// Auto layout devices in a grid
const buildLayout = (devices) => {
  const cols = 4;
  const xGap = 220;
  const yGap = 180;

  const nodes = devices.map((device, index) => ({
    id: String(device.id),
    type: "deviceNode",
    position: {
      x: (index % cols) * xGap + 50,
      y: Math.floor(index / cols) * yGap + 50,
    },
    data: {
      name: device.name,
      ipAddress: device.ipAddress,
      status: device.status,
      latency: device.latency,
      type: device.type,
    },
  }));

  // Auto connect routers/switches to servers/access points
  const edges = [];
  const routers = devices.filter((d) =>
    ["router", "firewall"].includes(d.type)
  );
  const others = devices.filter(
    (d) => !["router", "firewall"].includes(d.type)
  );

  routers.forEach((router) => {
    others.forEach((device) => {
      edges.push({
        id: `e-${router.id}-${device.id}`,
        source: String(router.id),
        target: String(device.id),
        animated: device.status === "ONLINE",
        style: {
          stroke: device.status === "ONLINE" ? "#16a34a" : "#dc2626",
          strokeWidth: 2,
        },
      });
    });
  });

  return { nodes, edges };
};

const Topology = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const { monitoringData } = useSocket();

  const fetchDevices = useCallback(async () => {
    try {
      const res = await API.get("/devices");
      const { nodes: n, edges: e } = buildLayout(res.data);
      setNodes(n);
      setEdges(e);
    } catch {
      setNodes([]);
      setEdges([]);
    } finally {
      setLoading(false);
    }
  }, [setNodes, setEdges]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Update node statuses when monitoring data comes in
  useEffect(() => {
    if (monitoringData.length > 0) {
      setNodes((nds) =>
        nds.map((node) => {
          const update = monitoringData.find(
            (d) => String(d.deviceId) === node.id
          );
          if (update) {
            return {
              ...node,
              data: {
                ...node.data,
                status: update.status,
                latency: update.latency,
              },
            };
          }
          return node;
        })
      );
    }
  }, [monitoringData, setNodes]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Network Topology
          </h2>
          <p className="text-sm text-gray-500">
            Live visual map of your network
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-gray-600">Online</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-gray-600">Offline</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            <span className="text-gray-600">Unknown</span>
          </div>
        </div>
      </div>

      {/* Device Type Legend */}
      <div className="bg-white rounded-xl shadow p-3 mb-4 flex items-center gap-6 text-xs text-gray-600 flex-wrap">
        {[
          { icon: "🔀", label: "Router" },
          { icon: "🔁", label: "Switch" },
          { icon: "🖥️", label: "Server" },
          { icon: "📡", label: "Access Point" },
          { icon: "🛡️", label: "Firewall" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1">
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
        <p className="text-gray-400 ml-auto">
          Drag devices to rearrange • Scroll to zoom
        </p>
      </div>

      {/* Topology Canvas */}
      <div className="bg-white rounded-xl shadow overflow-hidden" style={{ height: "600px" }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-gray-400">
            Loading topology...
          </div>
        ) : nodes.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <p className="text-4xl mb-2">🌐</p>
              <p>No devices registered yet</p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
          >
            <Controls />
            <MiniMap
              nodeColor={(node) => {
                const status = node.data?.status;
                return status === "ONLINE"
                  ? "#16a34a"
                  : status === "OFFLINE"
                  ? "#dc2626"
                  : "#9ca3af";
              }}
            />
            <Background variant="dots" gap={16} size={1} />
          </ReactFlow>
        )}
      </div>
    </Layout>
  );
};

export default Topology;